import React from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import API from "../API";

const normalizePermissions = (permissions) =>
  [...new Set((permissions || []).map(Number))].sort((a, b) => a - b);

const getWorkerName = (worker) =>
  worker?.name ||
  worker?.fullName ||
  [worker?.firstName, worker?.lastName].filter(Boolean).join(" ") ||
  worker?.email ||
  worker?.phone ||
  worker?._id ||
  "—";

const getWorkerEmail = (worker) => worker?.email || worker?.mail || "—";

const getWorkerPhone = (worker) => worker?.phone || worker?.gsm || "—";

const isEditingSelfAdmin = (worker) =>
  String(worker?._id || "") === String(API.user?._id || "") &&
  normalizePermissions(worker?.permissions).includes(0);

const getPermissionLabel = (permission) =>
  `${permission}. ${API.getPermissionRuleLabel(permission)}`;

const getPermissionSummary = (worker) => {
  const permissions = normalizePermissions(worker?.permissions);

  if (permissions.includes(-1)) {
    return {
      primary: "Deaktif",
      secondary: "Giriş kapalı",
      tone: "danger",
    };
  }

  if (permissions.includes(0)) {
    return {
      primary: "Admin",
      secondary:
        permissions.length > 1 ? `+${permissions.length - 1} ek yetki` : "Tam erişim",
      tone: "info",
    };
  }

  if (permissions.length === 0) {
    return {
      primary: "Yetki yok",
      secondary: "Atama yapılmadı",
      tone: "neutral",
    };
  }

  return {
    primary: API.getPermissionRuleLabel(permissions[0]),
    secondary:
      permissions.length > 1 ? `+${permissions.length - 1} ek yetki` : "Tek yetki",
    tone: "neutral",
  };
};

const getWorkerInitials = (worker) => {
  const name = getWorkerName(worker);
  if (!name || name === "—") return "W";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const isWorkerDeactivated = (worker) =>
  normalizePermissions(worker?.permissions).includes(-1);

const getWorkerStatusMeta = (worker) => {
  if (isWorkerDeactivated(worker)) {
    return {
      label: "Deaktif",
      backgroundColor: "#FEF2F2",
      color: "#991B1B",
      borderColor: "#FECACA",
    };
  }

  if (normalizePermissions(worker?.permissions).includes(0)) {
    return {
      label: "Admin",
      backgroundColor: "#EEF2FF",
      color: "#3730A3",
      borderColor: "#C7D2FE",
    };
  }

  return {
    label: "Aktif",
    backgroundColor: "#ECFDF5",
    color: "#166534",
    borderColor: "#BBF7D0",
  };
};

export default class WorkersAccessScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      workers: [],
      workersLoading: true,
      selectedWorkerId: undefined,
      selectedWorker: undefined,
      permissionDraft: [],
      detailsLoading: false,
      saving: false,
      detailsOpen: false,
    };
  }

  componentDidMount() {
    this.loadWorkers();
  }

  loadWorkers() {
    this.setState({ workersLoading: true });

    API.fetch("getworkers", {}).then((response) => {
      const workers = response?.workers || response || [];
      this.setState({ workers, workersLoading: false });
    });
  }

  openWorker(workerId) {
    this.setState({
      selectedWorkerId: workerId,
      detailsLoading: true,
      selectedWorker: undefined,
      detailsOpen: true,
    });

    API.fetch("getworkerdetails", { _id: workerId }).then((response) => {
      const worker = response?.worker || response;
      this.setState({
        selectedWorker: worker,
        permissionDraft: normalizePermissions(worker?.permissions),
        detailsLoading: false,
      });
    });
  }

  closeWorkerModal() {
    this.setState({
      detailsOpen: false,
      selectedWorkerId: undefined,
      selectedWorker: undefined,
      permissionDraft: [],
      detailsLoading: false,
      saving: false,
    });
  }

  togglePermission(permission) {
    this.setState((prevState) => {
      const exists = prevState.permissionDraft.includes(permission);
      const permissionDraft = exists
        ? prevState.permissionDraft.filter((x) => x !== permission)
        : [...prevState.permissionDraft, permission];

      return { permissionDraft: permissionDraft.sort((a, b) => a - b) };
    });
  }

  savePermissions() {
    if (!this.state.selectedWorker?._id) return;

    this.setState({ saving: true });
    const permissions = normalizePermissions(this.state.permissionDraft);

    API.fetch("saveworkerpermissions", {
      _id: this.state.selectedWorker._id,
      permissions,
    }).then((response) => {
      const worker = response?.worker || {
        ...this.state.selectedWorker,
        permissions,
      };

      this.setState((prevState) => ({
        saving: false,
        selectedWorker: worker,
        permissionDraft: normalizePermissions(worker.permissions),
        workers: prevState.workers.map((item) =>
          item._id === worker._id ? { ...item, ...worker } : item,
        ),
      }));
    });
  }

  deactivateWorker() {
    if (!this.state.selectedWorker?._id) return;

    this.setState({ saving: true });

    API.fetch("saveworkerpermissions", {
      _id: this.state.selectedWorker._id,
      permissions: [-1],
    }).then((response) => {
      const worker = response?.worker || {
        ...this.state.selectedWorker,
        permissions: [-1],
      };

      this.setState((prevState) => ({
        saving: false,
        selectedWorker: worker,
        permissionDraft: [-1],
        workers: prevState.workers.map((item) =>
          item._id === worker._id ? { ...item, ...worker } : item,
        ),
      }));
    });
  }

  renderPermissionEditor() {
    if (this.state.detailsLoading) {
      return (
        <div
          style={{
            minHeight: 360,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    if (!this.state.selectedWorker) {
      return null;
    }

    const worker = this.state.selectedWorker;
    const disableAdminToggle = isEditingSelfAdmin(worker);
    const selectedPermissions = normalizePermissions(worker.permissions);
    const isDeactivated = selectedPermissions.includes(-1);

    return (
      <Dialog
        open={this.state.detailsOpen}
        onClose={() => this.closeWorkerModal()}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          style: {
            borderRadius: 18,
            overflow: "hidden",
            maxHeight: "88vh",
            display: "flex",
            flexDirection: "column",
            background:
              "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 22%, #FFFFFF 100%)",
          },
        }}
      >
        <div
          style={{
            overflowY: "auto",
            flex: 1,
          }}
        >
          <div
            style={{
              padding: 28,
              background:
                "radial-gradient(circle at top left, #DBEAFE 0%, #EFF6FF 28%, rgba(255,255,255,0) 64%)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
              }}
            >
              <div>
                <Typography variant="overline" style={{ color: "#2563EB" }}>
                  Kullanıcı Yetkileri
                </Typography>
                <Typography variant="h4">{getWorkerName(worker)}</Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 6 }}
                >
                  {worker._id}
                </Typography>
              </div>

              <IconButton onClick={() => this.closeWorkerModal()}>
                <CloseIcon />
              </IconButton>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 24,
              }}
            >
              <DetailItem label="Ad Soyad" value={getWorkerName(worker)} />
              <DetailItem label="E-posta" value={getWorkerEmail(worker)} />
              <DetailItem label="Telefon" value={getWorkerPhone(worker)} />
              <DetailItem
                label="Atanmış Kural Sayısı"
                value={String(selectedPermissions.length || 0)}
              />
            </div>

            <div style={{ marginTop: 20 }}>
              <Typography variant="body2" color="textSecondary">
                Mevcut Yetki Etiketleri
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 10,
                }}
              >
                {selectedPermissions.length > 0 ? (
                  selectedPermissions.map((permission) => (
                    <Chip
                      key={permission}
                      label={getPermissionLabel(permission)}
                      style={{
                        backgroundColor: "#E0E7FF",
                        color: "#312E81",
                        fontWeight: 600,
                      }}
                    />
                  ))
                ) : (
                  <Chip label="Yetki atanmadı" variant="outlined" />
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: 28, paddingTop: 20 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <div>
              <Typography variant="h6">Yetki Kuralları</Typography>
              <Typography variant="body2" color="textSecondary">
                Her kuralın kapsamı açıklamasıyla birlikte listelenir.
              </Typography>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                variant="outlined"
                color={isDeactivated ? "success" : "error"}
                onClick={() =>
                  isDeactivated
                    ? this.setState({ permissionDraft: [] })
                    : this.deactivateWorker()
                }
                disabled={this.state.saving || disableAdminToggle}
              >
                {isDeactivated ? "Yeniden Aktif Et" : "Deaktif Et"}
              </Button>
              <Button
                variant="contained"
                onClick={() => this.savePermissions()}
                disabled={this.state.saving}
              >
                {this.state.saving ? "Kaydediliyor..." : "Yetkileri Kaydet"}
              </Button>
            </div>
          </div>

          {isDeactivated && (
            <div
              style={{
                marginBottom: 14,
                padding: 14,
                borderRadius: 12,
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
              }}
            >
              <Typography
                variant="body1"
                style={{ fontWeight: 700, color: "#991B1B" }}
              >
                Bu hesap deaktif.
              </Typography>
              <Typography variant="body2" style={{ color: "#7F1D1D" }}>
                `-1` izni atanmış hesaplar panele giriş yapamaz.
              </Typography>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {API.PERMISSION_RULES.map((rule) => {
              const selected = this.state.permissionDraft.includes(rule.id);

              return (
                <div
                  key={rule.id}
                  style={{
                    border: selected
                      ? "1px solid #93C5FD"
                      : "1px solid #E5E7EB",
                    borderRadius: 14,
                    padding: 14,
                    backgroundColor: selected ? "#EFF6FF" : "#FFFFFF",
                    boxShadow: selected
                      ? "0 8px 24px rgba(59, 130, 246, 0.12)"
                      : "none",
                  }}
                >
                  <FormControlLabel
                    style={{ alignItems: "flex-start", margin: 0 }}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => this.togglePermission(rule.id)}
                        disabled={
                          (disableAdminToggle && rule.id === 0) ||
                          (disableAdminToggle && rule.id === -1)
                        }
                        style={{ paddingTop: 0 }}
                      />
                    }
                    label={
                      <div>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 700, color: "#0F172A" }}
                        >
                          {rule.id}. {rule.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginTop: 4, lineHeight: 1.5 }}
                        >
                          {rule.description}
                        </Typography>
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>

          {disableAdminToggle && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 14 }}
            >
              Kendi admin (`0`) yetkinizi bu ekrandan kaldıramazsınız.
            </Typography>
          )}
        </div>
        </div>
      </Dialog>
    );
  }

  render() {
    const workers = this.state.workers;
    const activeWorkers = workers.filter((worker) => !isWorkerDeactivated(worker));
    const deactivatedWorkers = workers.filter((worker) =>
      isWorkerDeactivated(worker),
    );
    const adminWorkers = workers.filter((worker) =>
      normalizePermissions(worker.permissions).includes(0),
    );

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%" }}>
          <Typography variant="h4">Kullanıcılar ve Yetkiler</Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            Admin kullanıcılar çalışan hesaplarını görüntüleyebilir ve izin
            kurallarını düzenleyebilir.
          </Typography>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginTop: 24,
            }}
          >
            <MetricCard
              label="Toplam Çalışan"
              value={String(workers.length)}
              tone="default"
            />
            <MetricCard
              label="Aktif Hesap"
              value={String(activeWorkers.length)}
              tone="success"
            />
            <MetricCard
              label="Admin Kullanıcı"
              value={String(adminWorkers.length)}
              tone="info"
            />
            <MetricCard
              label="Deaktif Hesap"
              value={String(deactivatedWorkers.length)}
              tone="danger"
            />
          </div>

          <Badge style={{ marginTop: 30, padding: 0 }}>
            <div
              style={{
                padding: 28,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                borderBottom: "1px solid #EEF2F7",
                background:
                  "linear-gradient(135deg, #FAFCFF 0%, #F8FAFC 55%, #FFFFFF 100%)",
              }}
            >
              <div>
                <Typography variant="h5" style={{ color: "#0F172A" }}>
                  Çalışan Dizini
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 6 }}
                >
                  Yetki durumları, iletişim bilgileri ve erişim seviyeleri tek
                  listede görünür.
                </Typography>
              </div>

              <Button
                variant="outlined"
                onClick={() => this.loadWorkers()}
                disabled={this.state.workersLoading}
                style={{
                  borderColor: "#CBD5E1",
                  color: "#0F172A",
                  backgroundColor: "white",
                }}
              >
                Yenile
              </Button>
            </div>

            {this.state.workersLoading ? (
              <div style={{ padding: 24 }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                autoHeight
                getRowId={(row) => row._id}
                rows={this.state.workers}
                columns={[
                  {
                    field: "name",
                    headerName: "Ad Soyad",
                    flex: 1.15,
                    minWidth: 260,
                    sortable: false,
                    renderCell: (params) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          minWidth: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <Avatar
                          style={{
                            width: 42,
                            height: 42,
                            background:
                              "linear-gradient(135deg, #0F172A 0%, #334155 100%)",
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          {getWorkerInitials(params.row)}
                        </Avatar>
                        <div
                          style={{
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="body1"
                            style={{
                              fontWeight: 700,
                              color: "#0F172A",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {getWorkerName(params.row)}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748B",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              marginTop: 2,
                            }}
                          >
                            {params.row._id}
                          </Typography>
                        </div>
                      </div>
                    ),
                  },
                  {
                    field: "email",
                    headerName: "E-posta",
                    flex: 0.95,
                    minWidth: 220,
                    sortable: false,
                    renderCell: (params) => (
                      <div
                        style={{
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: "#0F172A",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {getWorkerEmail(params.row)}
                        </Typography>
                      </div>
                    ),
                  },
                  {
                    field: "phone",
                    headerName: "Telefon",
                    flex: 0.7,
                    minWidth: 150,
                    sortable: false,
                    renderCell: (params) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ color: "#334155", fontWeight: 500 }}
                        >
                          {getWorkerPhone(params.row)}
                        </Typography>
                      </div>
                    ),
                  },
                  {
                    field: "status",
                    headerName: "Durum",
                    flex: 0.55,
                    minWidth: 130,
                    sortable: false,
                    renderCell: (params) => {
                      const status = getWorkerStatusMeta(params.row);

                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              border: `1px solid ${status.borderColor}`,
                              backgroundColor: status.backgroundColor,
                              color: status.color,
                              borderRadius: 999,
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {status.label}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    field: "permissions",
                    headerName: "Yetkiler",
                    flex: 0.95,
                    minWidth: 210,
                    sortable: false,
                    renderCell: (params) => {
                      const summary = getPermissionSummary(params.row);
                      const toneStyles = {
                        info: {
                          backgroundColor: "#EEF2FF",
                          borderColor: "#C7D2FE",
                          primaryColor: "#3730A3",
                          secondaryColor: "#4F46E5",
                        },
                        danger: {
                          backgroundColor: "#FEF2F2",
                          borderColor: "#FECACA",
                          primaryColor: "#991B1B",
                          secondaryColor: "#B91C1C",
                        },
                        neutral: {
                          backgroundColor: "#F8FAFC",
                          borderColor: "#E2E8F0",
                          primaryColor: "#334155",
                          secondaryColor: "#64748B",
                        },
                      };

                      const style = toneStyles[summary.tone] || toneStyles.neutral;

                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              border: `1px solid ${style.borderColor}`,
                              backgroundColor: style.backgroundColor,
                              borderRadius: 14,
                              padding: "8px 10px",
                              overflow: "hidden",
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: style.primaryColor,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {summary.primary}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                color: style.secondaryColor,
                                display: "block",
                                marginTop: 2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {summary.secondary}
                            </Typography>
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    field: "action",
                    headerName: "",
                    flex: 0.4,
                    minWidth: 88,
                    sortable: false,
                    renderCell: (params) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        <Button
                          onClick={() => this.openWorker(params.row._id)}
                          variant="outlined"
                          style={{
                            minWidth: 0,
                            borderColor: "#CBD5E1",
                            color: "#0F172A",
                            borderRadius: 12,
                          }}
                        >
                          <ArrowForwardIcon style={{ fontSize: 18 }} />
                        </Button>
                      </div>
                    ),
                  },
                ]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                rowHeight={84}
                pageSizeOptions={[10, 25, 50]}
                onRowClick={(params) => this.openWorker(params.row._id)}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#F8FAFC",
                    borderRadius: 0,
                    borderBottom: "1px solid #E2E8F0",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                    color: "#475569",
                    fontSize: 13,
                  },
                  "& .MuiDataGrid-cell": {
                    alignItems: "center",
                    borderBottom: "1px solid #F1F5F9",
                    paddingTop: 0,
                    paddingBottom: 0,
                    display: "flex",
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                    backgroundColor: "#FFFFFF",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#F8FAFC",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid #E2E8F0",
                    backgroundColor: "#FCFDFE",
                  },
                }}
              />
            )}
          </Badge>

          {this.renderPermissionEditor()}
        </div>
      </div>
    );
  }
}

const DetailItem = (props) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 8,
      padding: 12,
      backgroundColor: "#F9FAFB",
    }}
  >
    <Typography variant="body2" color="textSecondary">
      {props.label}
    </Typography>
    <Typography variant="body1" style={{ marginTop: 4 }}>
      {props.value || "—"}
    </Typography>
  </div>
);

const MetricCard = ({ label, value, tone }) => {
  const tones = {
    default: {
      background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
      border: "#E2E8F0",
      value: "#0F172A",
    },
    success: {
      background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)",
      border: "#BBF7D0",
      value: "#166534",
    },
    info: {
      background: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
      border: "#BFDBFE",
      value: "#1D4ED8",
    },
    danger: {
      background: "linear-gradient(135deg, #FEF2F2 0%, #FFF7F7 100%)",
      border: "#FECACA",
      value: "#B91C1C",
    },
  };

  const selectedTone = tones[tone] || tones.default;

  return (
    <div
      style={{
        border: `1px solid ${selectedTone.border}`,
        background: selectedTone.background,
        borderRadius: 18,
        padding: 18,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography variant="body2" style={{ color: "#64748B" }}>
        {label}
      </Typography>
      <Typography
        variant="h4"
        style={{ marginTop: 10, color: selectedTone.value, fontWeight: 800 }}
      >
        {value}
      </Typography>
    </div>
  );
};

const Badge = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
        borderRadius: 8,
        padding: 24,
        color: "rgb(18, 24, 40)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};
